import React, { useMemo, useState } from 'react';
import { X, Save, Trash2, Loader2 } from 'lucide-react';
import api from '../../api/auth.api';
import { API_CONFIG } from '../../config/api.config';
import { Job } from '../../types/backend.types';
import { TimeEntryRow } from './timecard.types';
import { CustomTimePicker } from '../common/CustomTimePicker';
import { easternOffsetMinutes, easternWallClockToUTC, isoToEasternDate, isoToEasternTime, resolveClockOut, nextDayConfirmMessage } from '../../utils/easternTime';

interface Props {
  employeeId: number;
  employeeName: string;
  entry: TimeEntryRow | null;   // null = create a new entry
  defaultDate: string;          // YYYY-MM-DD (used for create / prefill)
  jobs: Job[];
  adminName: string;
  onClose: () => void;
  onSaved: () => void;          // refetch the week after a successful mutation
}

// Prefill the date/time fields from a UTC timestamp using the Eastern wall clock
// (not the viewer's local time), so an admin in any timezone sees the real
// Eastern values they would edit.
const isoToLocalDate = (iso?: string | null) => isoToEasternDate(iso);
const isoToLocalTime = (iso?: string | null) => isoToEasternTime(iso);

export const EditEntryModal: React.FC<Props> = ({
  employeeId, employeeName, entry, defaultDate, jobs, adminName, onClose, onSaved,
}) => {
  const isEdit = !!entry;

  const [date, setDate] = useState(entry ? isoToLocalDate(entry.clock_in) : defaultDate);
  const [clockIn, setClockIn] = useState(entry ? isoToLocalTime(entry.clock_in) : '07:00');
  const [clockOut, setClockOut] = useState(entry ? isoToLocalTime(entry.clock_out) : '');
  const [jobId, setJobId] = useState<string>(entry ? String(entry.job_id) : '');
  const [lunchTaken, setLunchTaken] = useState<boolean>(entry ? !!entry.lunch_taken : false);
  const [lunchMinutes, setLunchMinutes] = useState<number>(entry?.lunch_minutes ?? 30);
  const [reason, setReason] = useState('');

  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sortedJobs = useMemo(
    () => [...jobs].sort((a, b) => (a.job_name || '').localeCompare(b.job_name || '')),
    [jobs],
  );

  // Live preview of the shift so an AM/PM mistake (e.g. 7 PM instead of 7 AM,
  // which silently rolls to a ~22h overnight) is obvious before saving.
  const preview = useMemo(() => {
    if (!date || !clockIn || !clockOut) return null;
    const ci = new Date(`${date}T${clockIn}`);
    let co = new Date(`${date}T${clockOut}`);
    let overnight = false;
    if (co <= ci) { co = new Date(co.getTime() + 24 * 60 * 60 * 1000); overnight = true; }
    const grossMin = Math.round((co.getTime() - ci.getTime()) / 60000);
    const netMin = Math.max(0, grossMin - (lunchTaken ? lunchMinutes : 0));
    return { grossMin, netMin, overnight };
  }, [date, clockIn, clockOut, lunchTaken, lunchMinutes]);

  const hm = (min: number) => `${Math.floor(min / 60)}:${String(min % 60).padStart(2, '0')}`;
  const longShift = !!preview && preview.grossMin > 16 * 60;

  const handleSave = async () => {
    setError(null);
    if (!jobId || !date || !clockIn) {
      setError('Job, date and clock-in are required.');
      return;
    }
    setSaving(true);
    try {
      const editReason = reason.trim() || `${isEdit ? 'Edited' : 'Created'} by ${adminName}`;

      if (isEdit && entry) {
        // Interpret the typed date/time as Eastern wall clock -> UTC (independent
        // of the admin's own timezone).
        const clockInDateTime = easternWallClockToUTC(date, clockIn);
        let clockOutDateTime: Date | null = null;
        if (clockOut) {
          // Rounded-equal times are a valid 0-hour entry; a genuinely earlier
          // time means next day and must be confirmed, never silently assumed.
          const resolved = resolveClockOut(clockInDateTime.toISOString(), easternWallClockToUTC(date, clockOut));
          if (resolved.nextDay && !window.confirm(nextDayConfirmMessage(clockOut, clockInDateTime.toISOString()))) {
            setSaving(false);
            return;
          }
          clockOutDateTime = resolved.date;
        }
        const res = await api.put(API_CONFIG.ENDPOINTS.EDIT_TIME, {
          entry_id: entry.entry_id,
          job_id: parseInt(jobId, 10),
          clock_in: clockInDateTime.toISOString(),
          clock_out: clockOutDateTime ? clockOutDateTime.toISOString() : null,
          lunch_taken: lunchTaken,
          lunch_minutes: lunchTaken ? lunchMinutes : 0,
          shop_time_minutes: entry.shop_time_minutes ?? 0,
          edit_reason: editReason,
        });
        if (!res.data.success) throw new Error(res.data.error || 'Failed to save entry');
      } else {
        // Create — interpret the typed time as Eastern: send the Eastern offset
        // (not the viewer's) so the backend's wallClock+offset math yields the
        // intended Eastern time regardless of where the admin is.
        const timezoneOffset = easternOffsetMinutes(new Date(`${date}T12:00:00Z`));
        let clockOutISO: string | null = null;
        if (clockOut) {
          const offsetMs = timezoneOffset * 60 * 1000;
          const ciUTC = new Date(new Date(`${date}T${clockIn}:00Z`).getTime() + offsetMs);
          const coUTC = new Date(new Date(`${date}T${clockOut}:00Z`).getTime() + offsetMs);
          // Rounded-equal times are a valid 0-hour entry; a genuinely earlier
          // time means next day and must be confirmed, never silently assumed.
          const resolved = resolveClockOut(ciUTC.toISOString(), coUTC);
          if (resolved.nextDay && !window.confirm(nextDayConfirmMessage(clockOut, ciUTC.toISOString()))) {
            setSaving(false);
            return;
          }
          clockOutISO = resolved.date.toISOString();
        }
        const res = await api.post(API_CONFIG.ENDPOINTS.CREATE_TIME_ENTRY, {
          employee_id: employeeId,
          job_id: parseInt(jobId, 10),
          clock_in_date: date,
          clock_in_time: clockIn,
          timezone_offset_minutes: timezoneOffset,
          clock_out: clockOutISO,
          lunch_taken: lunchTaken,
          lunch_minutes: lunchTaken ? lunchMinutes : 0,
          edit_reason: editReason,
        });
        if (!res.data.success) throw new Error(res.data.error || 'Failed to create entry');
      }
      onSaved();
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Something went wrong.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!entry) return;
    setDeleting(true);
    setError(null);
    try {
      const res = await api.delete(API_CONFIG.ENDPOINTS.DELETE_TIME_ENTRY(entry.entry_id));
      if (!res.data.success) throw new Error(res.data.error || 'Failed to delete entry');
      onSaved();
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to delete entry.');
      setDeleting(false);
    }
  };

  const fieldLabel: React.CSSProperties = { fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 6, display: 'block' };
  const fieldInput: React.CSSProperties = { width: '100%', minWidth: 0, maxWidth: '100%', padding: '12px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 16, boxSizing: 'border-box', background: '#fff', WebkitAppearance: 'none', appearance: 'none' };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 1000,
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', width: '100%', maxWidth: 520, borderRadius: '16px 16px 0 0',
          maxHeight: '92vh', overflowY: 'auto', padding: '18px 18px 28px',
          boxShadow: '0 -8px 30px rgba(0,0,0,0.2)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#1f2937' }}>
              {isEdit ? 'Edit Entry' : 'Add Entry'}
            </div>
            <div style={{ fontSize: 13, color: '#64748b' }}>{employeeName}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6 }}>
            <X size={22} color="#64748b" />
          </button>
        </div>

        {error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', borderRadius: 8, padding: '10px 12px', fontSize: 14, marginBottom: 12 }}>
            {error}
          </div>
        )}

        <div style={{ marginBottom: 14 }}>
          <label style={fieldLabel}>Job</label>
          <select value={jobId} onChange={(e) => setJobId(e.target.value)} style={fieldInput}>
            <option value="">— Select job —</option>
            {sortedJobs.map((j) => (
              <option key={j.job_id} value={j.job_id}>
                {j.job_name}{j.bradanna_job_number ? ` · ${j.bradanna_job_number}` : ''}
              </option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={fieldLabel}>Date</label>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={fieldInput} />
        </div>

        <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <label style={fieldLabel}>Clock In</label>
            <CustomTimePicker value={clockIn} onChange={setClockIn} label="Clock In" style={fieldInput} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <label style={fieldLabel}>Clock Out</label>
            <CustomTimePicker value={clockOut} onChange={setClockOut} label="Clock Out" style={fieldInput} />
          </div>
        </div>
        {!clockOut && (
          <div style={{ fontSize: 12, color: '#b45309', marginTop: -6, marginBottom: 12 }}>
            Leave clock-out blank only if still open — open entries can't be approved.
          </div>
        )}
        {preview && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
            marginTop: -2, marginBottom: 12, padding: '8px 10px', borderRadius: 8,
            background: longShift ? '#fef2f2' : '#f8fafc',
            border: `1px solid ${longShift ? '#fecaca' : '#eef2f7'}`,
          }}>
            <span style={{ fontSize: 13, color: longShift ? '#dc2626' : '#475569', fontWeight: 600 }}>
              {longShift ? '⚠ ' : ''}{hm(preview.netMin)} worked{preview.overnight ? ' · overnight (next day)' : ''}
            </span>
            {longShift && (
              <span style={{ fontSize: 12, color: '#dc2626' }}>Check AM/PM</span>
            )}
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
          <label style={{ ...fieldLabel, marginBottom: 0 }}>Lunch deducted</label>
          <button
            onClick={() => setLunchTaken((v) => !v)}
            style={{
              padding: '8px 14px', borderRadius: 20, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 14,
              background: lunchTaken ? '#667eea' : '#e5e7eb', color: lunchTaken ? '#fff' : '#475569',
            }}
          >
            {lunchTaken ? 'Yes' : 'No'}
          </button>
          {lunchTaken && (
            <input
              type="number" value={lunchMinutes} min={0} step={5}
              onChange={(e) => setLunchMinutes(parseInt(e.target.value, 10) || 0)}
              style={{ ...fieldInput, width: 90 }}
            />
          )}
          {lunchTaken && <span style={{ fontSize: 13, color: '#64748b' }}>min</span>}
        </div>

        <div style={{ marginBottom: 18 }}>
          <label style={fieldLabel}>Reason (optional)</label>
          <input
            type="text" value={reason} placeholder={`${isEdit ? 'Edited' : 'Created'} by ${adminName}`}
            onChange={(e) => setReason(e.target.value)} style={fieldInput}
          />
        </div>

        <button
          onClick={handleSave}
          disabled={saving || deleting}
          style={{
            width: '100%', padding: '14px', borderRadius: 10, border: 'none', cursor: 'pointer',
            background: '#667eea', color: '#fff', fontSize: 16, fontWeight: 700,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, opacity: saving ? 0.7 : 1,
          }}
        >
          {saving ? <Loader2 size={18} className="spin" /> : <Save size={18} />}
          {isEdit ? 'Save Changes' : 'Create Entry'}
        </button>

        {isEdit && (
          confirmDelete ? (
            <div style={{ marginTop: 12, display: 'flex', gap: 10 }}>
              <button
                onClick={handleDelete}
                disabled={deleting}
                style={{ flex: 1, padding: '12px', borderRadius: 10, border: 'none', cursor: 'pointer', background: '#dc2626', color: '#fff', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
              >
                {deleting ? <Loader2 size={16} className="spin" /> : <Trash2 size={16} />} Confirm delete
              </button>
              <button
                onClick={() => setConfirmDelete(false)}
                disabled={deleting}
                style={{ flex: 1, padding: '12px', borderRadius: 10, border: '1px solid #e5e7eb', cursor: 'pointer', background: '#fff', color: '#475569', fontWeight: 600 }}
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={() => setConfirmDelete(true)}
              disabled={saving}
              style={{ width: '100%', marginTop: 12, padding: '12px', borderRadius: 10, border: '1px solid #fecaca', cursor: 'pointer', background: '#fff', color: '#dc2626', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
            >
              <Trash2 size={16} /> Delete entry
            </button>
          )
        )}
      </div>
    </div>
  );
};
